import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GraduationCap, User, LogOut, Database, Target, Users, FileText, Building, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const location = useLocation();
  const { user, signOut, isAdmin, isAdminOnly } = useAuth();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'College Reviews', path: '/college-reviews' },
    { name: 'NEET Predictor', path: '/neet-college-predictor' },
    // { name: 'JoSAA Predictor', path: '/josaa-personalized-list' }, 
    { name: 'Blog', path: '/blog' },
    { name: 'Student Connect', path: '/student-connect' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleAuthClick = (mode: 'signin' | 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm fixed-top" style={{ minHeight: '70px' }}>
        <div className="container">
          {/* Brand with Graduation Cap */}
          <Link to="/" className="navbar-brand d-flex align-items-center py-2">
            <GraduationCap className="text-primary me-2" size={32} />
            <span className="fw-bold text-dark" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.5rem)' }}>Grad Vikalp</span>
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="navbar-toggler border-0 shadow-none p-2"
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Navigation Menu */}
          <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`}>
            <ul className="navbar-nav mx-auto d-flex gap-0 gap-md-1">
              {navItems.map((item) => (
                <li key={item.name} className="nav-item">
                  <Link
                    to={item.path}
                    className={`nav-link fw-medium px-2 px-md-3 py-2 rounded-lg transition-all ${
                      isActive(item.path) 
                        ? 'active text-primary bg-primary bg-opacity-10' 
                        : 'text-dark hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                    style={{ 
                      fontSize: 'clamp(0.9rem, 2.5vw, 0.95rem)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Auth Section */}
            <div className="d-flex align-items-center gap-2 mt-2 mt-lg-0">
              {user ? (
                <div className="dropdown">
                  <button
                    className="btn btn-outline-primary dropdown-toggle d-flex align-items-center gap-2 w-100 w-lg-auto"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ minHeight: '40px' }}
                  >
                    <User size={16} />
                    <span>{user.full_name}</span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <span className="dropdown-item-text small text-muted">
                        {user.email}
                      </span>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    {isAdmin && (
                      <>
                        <li>
                          <Link 
                            to="/manage-colleges" 
                            className="dropdown-item d-flex align-items-center gap-2"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Database size={16} />
                            Review Colleges
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/manage-personalized-colleges" 
                            className="dropdown-item d-flex align-items-center gap-2"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Target size={16} />
                            Personalized Colleges
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/admin/personalized-lists"
                            className="dropdown-item d-flex align-items-center gap-2"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Users size={16} />
                            NEET Submissions
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/admin/josaa-manager" 
                            className="dropdown-item d-flex align-items-center gap-2"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Target size={16} />
                            JoSAA Manager
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/admin/blog-manager" 
                            className="dropdown-item d-flex align-items-center gap-2"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <FileText size={16} />
                            Blog Manager
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/admin/college-manager" 
                            className="dropdown-item d-flex align-items-center gap-2"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Building size={16} />
                            College Manager
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/admin/seo-manager" 
                            className="dropdown-item d-flex align-items-center gap-2"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Globe size={16} />
                            SEO Manager
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/admin" 
                            className="dropdown-item d-flex align-items-center gap-2"
                            onClick={() => setIsMenuOpen(false)} 
                          >
                            <Users size={16} />
                            Admin Panel {!isAdminOnly && <small className="text-muted">(Limited Access)</small>}
                          </Link>
                        </li>
                        <li><hr className="dropdown-divider" /></li>
                      </>
                    )}
                    <li>
                      <button 
                        onClick={handleSignOut}
                        className="dropdown-item d-flex align-items-center gap-2 text-danger"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => handleAuthClick('signin')}
                    className="btn btn-outline-primary w-100 w-lg-auto mb-2 mb-lg-0"
                    style={{ 
                      fontSize: 'clamp(0.9rem, 2.5vw, 0.95rem)',
                      transition: 'all 0.2s ease',
                      minHeight: '40px'
                    }}
                  >
                    Sign In
                  </button>
                  <Link 
                    to="/personalized-list"
                    className="btn btn-primary px-3 px-md-4 py-2 fw-semibold rounded-lg shadow-sm w-100 w-lg-auto"
                    style={{ 
                      fontSize: 'clamp(0.9rem, 2.5vw, 0.95rem)',
                      transition: 'all 0.2s ease',
                      minHeight: '40px'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />
    </>
  );
};

export default Navbar;